import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';

import type { ToastData } from '~/hooks/useToast';
import useToast from '~/hooks/useToast';

interface UseAvatarUploadOptions {
  onSuccess?: () => void;
}

interface UseAvatarUploadReturn {
  uploadAvatar: (file: File) => void;
  isUploading: boolean;
  optimisticAvatarUrl: string | null;
}

/**
 * Hook for immediate avatar upload with optimistic UI updates.
 * Uses the existing /profile action route which handles multipart form data.
 */
const useAvatarUpload = ({ onSuccess }: UseAvatarUploadOptions = {}): UseAvatarUploadReturn => {
  const { t } = useTranslation();
  const fetcher = useFetcher<{ toast?: ToastData }>();
  const [optimisticAvatarUrl, setOptimisticAvatarUrl] = useState<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const isUploading = fetcher.state === 'submitting';

  useToast(fetcher.data?.toast);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.toast?.status === 'success') {
      onSuccessRef.current?.();
    }
    if (fetcher.state === 'idle' && fetcher.data?.toast?.status === 'error') {
      if (optimisticAvatarUrl) {
        URL.revokeObjectURL(optimisticAvatarUrl);
        setOptimisticAvatarUrl(null);
      }
    }
  }, [fetcher.state, fetcher.data, optimisticAvatarUrl]);

  const uploadAvatar = useCallback(
    (file: File) => {
      // Revoke previous blob URL to prevent memory leak
      if (optimisticAvatarUrl) {
        URL.revokeObjectURL(optimisticAvatarUrl);
      }

      // Create optimistic preview URL
      const previewUrl = URL.createObjectURL(file);
      setOptimisticAvatarUrl(previewUrl);

      // Build FormData for multipart upload
      const formData = new FormData();
      formData.append('avatar', file);
      formData.append('profile_success_message', t('toast.success'));
      formData.append('profile_error_message', t('errors.default'));

      fetcher.submit(formData, {
        method: 'PATCH',
        action: '/profile',
        encType: 'multipart/form-data',
      });
    },
    [fetcher, optimisticAvatarUrl, t],
  );

  // Cleanup optimistic URL when component unmounts
  useEffect(() => {
    return () => {
      if (optimisticAvatarUrl) {
        URL.revokeObjectURL(optimisticAvatarUrl);
      }
    };
  }, [optimisticAvatarUrl]);

  return {
    uploadAvatar,
    isUploading,
    optimisticAvatarUrl: optimisticAvatarUrl || null,
  };
};

export default useAvatarUpload;
